import { useRef, useState } from "react";
import { adminApi, API } from "@/lib/api";
import { Upload, Loader2, X, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { toast } from "sonner";

const BACKEND_BASE = API.replace(/\/api$/, "");

// Upload & manage a gallery of product images. First image is the primary/cover.
export const MultiImageUpload = ({ value = [], onChange, testId = "multi-image-upload" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const images = Array.isArray(value) ? value.filter(Boolean) : [];

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const added = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await adminApi().post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(data.url.startsWith("http") ? data.url : `${BACKEND_BASE}${data.url}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      }
    }
    if (added.length) {
      onChange([...images, ...added]);
      toast.success(`${added.length} image${added.length > 1 ? "s" : ""} added`);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (i) => onChange(images.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const makePrimary = (i) => {
    if (i === 0) return;
    const next = [...images];
    const [item] = next.splice(i, 1);
    next.unshift(item);
    onChange(next);
  };

  return (
    <div data-testid={testId}>
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3" data-testid="multi-image-grid">
          {images.map((src, i) => (
            <div key={src + i} className="relative group border border-gray-200 rounded-md overflow-hidden bg-surface" data-testid={`gallery-image-${i}`}>
              <img src={src} alt={`Image ${i + 1}`} className="w-full aspect-[3/4] object-cover" />
              {i === 0 && (
                <span className="absolute top-1 left-1 bg-pink text-white text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                  <Star className="w-2.5 h-2.5 fill-white" /> Cover
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-black/55 flex items-center justify-between px-1 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-white/90 disabled:opacity-30 hover:text-white" aria-label="Move left" data-testid={`gallery-move-left-${i}`}>
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {i !== 0 && (
                  <button type="button" onClick={() => makePrimary(i)} className="text-white/90 hover:text-white text-[9px] uppercase tracking-wide" data-testid={`gallery-make-cover-${i}`}>
                    Set cover
                  </button>
                )}
                <button type="button" onClick={() => move(i, 1)} disabled={i === images.length - 1} className="text-white/90 disabled:opacity-30 hover:text-white" aria-label="Move right" data-testid={`gallery-move-right-${i}`}>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <button type="button" onClick={() => removeAt(i)} data-testid={`gallery-remove-${i}`}
                className="absolute top-1 right-1 bg-white/90 text-charcoal rounded-full p-0.5 hover:bg-pink hover:text-white transition-colors" aria-label="Remove image">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
        data-testid="multi-image-add-btn"
        className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 py-6 text-gray-500 hover:border-pink hover:text-pink transition-colors disabled:opacity-60">
        {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
        <span className="text-xs uppercase tracking-[0.12em]">{uploading ? "Uploading..." : images.length ? "Add More Images" : "Attach Images"}</span>
        <span className="text-[11px] text-gray-400">Select one or more. First image is the cover. JPG, PNG, WEBP (max 8MB each)</span>
      </button>
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="multi-image-file-input" />
    </div>
  );
};
